import {
  ChangeDetectionStrategy, Component, ElementRef,
  computed, effect, inject, signal, viewChild,
} from '@angular/core';
import { queryWeekSummary } from '../../../core/services/week-summary-in-office';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  PaperButtonComponent,
  PaperDialogComponent,
  PaperErrorBannerComponent,
  PaperIconComponent,
  PaperInlineMessageComponent,
  PaperKbdComponent,
  PaperSpinnerComponent,
  PaperTagComponent,
} from '@ricoh-paper/angular';

import { QuickBookService } from './quick-book.service';
import { PlatformDataService } from '../../../core/services/platform-data.service';
import { LocationStateService } from '../../../core/services/location-state.service';
import { MAP_REFRESH } from '../../../core/map-engine/map-refresh.token';
import { GraphQLClientService } from '../../../core/services/graphql-client.service';
import { FeatureFlagsViewerService } from '../../../core/services/feature-flags-viewer.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../core/analytics/events';
import {
  AutoBookService,
  matchesAutoBookKind,
  type AutoBookPlan,
  type AutoBookProgress,
} from '../../../core/services/auto-book.service';
import {
  PRESENCE_OPTIONS,
  PresenceWriterService,
  presenceRefusal,
  type Presence,
} from '../../../core/services/presence-writer.service';
import { isErr, makeError, type ApiError } from '../../../core/models/api-result';
import { localDateStr } from '../../../core/utils/text';
import {
  SbSegmentedComponent,
  type SbSegment,
} from '../../../features/bookings/smart-book/sb-segmented.component';
import {
  applyPreset,
  applySmartFill,
  canFillDown,
  fillAcross,
  fillAcrossPressed,
  fillDown,
  focusedDate,
  holdsAnything,
  isEditable,
  isOfficeDay,
  officeDays,
  plannedBookings,
  presenceDeltas,
  presenceIsLocked,
  RESOURCE_KINDS,
  seedOfficeDefaults,
  toPlanDay,
  toggleResource,
  topColleagueDayLabels,
  weekRangeLabel,
  withConfirmedPresence,
  withPresence,
  type PlanDay,
  type ResourceKind,
  type SmartBookDayDto,
} from './smart-book-logic';

/** Localised outcome of a commit run, rendered in the result banner. */
interface CommitResult {
  readonly summary: string;
  readonly skipped: string;
  readonly failures: string[];
}

type CommitProgress =
  | AutoBookProgress
  | {
      readonly stage: 'presence';
      readonly completed: number;
      readonly total: number;
    };

const WEEK_SUMMARY_QUERY = `
  query MyWeekSummary($startDate: String!, $endDate: String!, $timezoneId: String) {
    myWeekSummary(startDate: $startDate, endDate: $endDate, timezoneId: $timezoneId) {
      locationName locationId
      days {
        date dayLabel isWeekend colleaguesInOffice
        bookings { _id bookingType spaceName floorName startTime endTime checkedIn spaceId }
        status { statusId statusName }
      }
    }
  }
`;

/** The same read with `inOfficeStatus` (API #974); `queryWeekSummary` falls back to the one above. */
const WEEK_SUMMARY_QUERY_IN_OFFICE = `
  query MyWeekSummaryInOffice($startDate: String!, $endDate: String!, $timezoneId: String) {
    myWeekSummary(startDate: $startDate, endDate: $endDate, timezoneId: $timezoneId) {
      locationName locationId
      days {
        inOfficeStatus
        date dayLabel isWeekend colleaguesInOffice
        bookings { _id bookingType spaceName floorName startTime endTime checkedIn spaceId }
        status { statusId statusName }
      }
    }
  }
`;

interface WeekSummaryDto {
  locationName?: string;
  locationId?: string;
  days?: SmartBookDayDto[];
}

/** The furthest week ahead the planner will step to. */
const MAX_WEEK_OFFSET = 3;

/**
 * im-smart-book-dialog — "Smart Book", the week planner before Plan my week.
 *
 * RETIRING (Nath, 4 Oct, P3). With `home-workspace-week` on, ⌘B, ⌘K, the tray and the widgets open Plan my week
 * (shared/plan-week) instead; `im-quick-book-dialog` (quick-book-dialog.component.ts) chooses which. This dialog
 * stays for everyone else until the flag is on for all, then goes.
 *
 * A CENTRED, SCRIMMED DIALOG (the Dialog family — not a popover, not a
 * column): it blocks the page because planning a week is one decision taken
 * in one pass. Opened from ⌘B, the ⌘K palette, the home "Plan my week"
 * button, the day-card popover, and every quick-book widget — see
 * {@link QuickBookService}.
 *
 * THE SURFACE
 *   Header      lightning mark · "Smart Book" · the week span and building ·
 *               the ⌘B keycap · close.
 *   Intro       what the planner does, plus Smart-fill.
 *   Fill across one resource applied to EVERY office day at once, with the
 *               office-day count it applies to. Disabled — and saying why —
 *               when there is no office day yet.
 *   Day rows    one per weekday: the date, a full-width Office · Home ·
 *               Elsewhere · Out four-way, and — on an office day — the
 *               resource chips plus Fill down. The focused day's whole row
 *               carries a brand tint. A PAST weekday keeps its row (control
 *               disabled, reason linked, holdings still shown) so the week
 *               reads as a week; weekends are inert.
 *   Footer      the derived "N office days · M bookings", Cancel, and a
 *               primary action that names the M it is about to book.
 *
 * EVERY NUMBER IS DERIVED (HANDOFF §11c). The office-day count, the booking
 * count, the primary action's number and the Fill-across pressed state all
 * come from `smart-book-logic` reading the SAME `PlanDay[]`. Nothing here
 * keeps a second tally, and nothing renders a placeholder count.
 *
 * NOTHING IS GUESSED. A resource the viewer already holds is named from the
 * booking record; a resource being REQUESTED is not named, because the space
 * is auto-picked at commit time and predicting it here would be a guess. Once
 * the commit lands the week reloads and the chip carries the real name. The
 * one default — a desk on an office day (§11b) — is not an exception to that:
 * it renders pressed, the footer counts it and the action names it, so the
 * viewer is looking at exactly what the commit will send.
 *
 * WRITE-THROUGH (WP21 step 2). Committing writes presence deltas through
 * `PresenceWriterService` and the resources through `AutoBookService` →
 * `createBookings` — the same holdings and presence source Home's week strip
 * and the Bookings page read. The plan is never a fourth source of truth.
 *
 * AI CONTEXT: `QuickBookService.start()` resolves true when ≥1 booking was
 * created during the session (callers refresh their surfaces on true). On a
 * clean commit the dialog closes itself; it stays open only when per-day
 * failures need reading.
 */
@Component({
  selector: 'im-smart-book-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    PaperButtonComponent,
    PaperDialogComponent,
    PaperErrorBannerComponent,
    PaperIconComponent,
    PaperInlineMessageComponent,
    PaperKbdComponent,
    PaperSpinnerComponent,
    PaperTagComponent,
    SbSegmentedComponent,
  ],
  templateUrl: './smart-book-dialog.component.html',
  styleUrl: './smart-book-dialog.component.scss',
  host: { class: 'ph-no-capture' },
})
export class SmartBookDialogComponent {
  protected readonly quickBookService = inject(QuickBookService);
  private readonly platformData = inject(PlatformDataService);
  private readonly locationState = inject(LocationStateService);
  private readonly orchestrator = inject(MAP_REFRESH);
  private readonly graphql = inject(GraphQLClientService);
  private readonly autoBook = inject(AutoBookService);
  private readonly presenceWriter = inject(PresenceWriterService);
  private readonly featureFlags = inject(FeatureFlagsViewerService);
  private readonly analytics = inject(AnalyticsService);
  private readonly transloco = inject(TranslocoService);
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  // ── State ──────────────────────────────────────────────
  protected readonly loading = signal(false);
  protected readonly loadError = signal<ApiError | null>(null);
  protected readonly days = signal<PlanDay[]>([]);
  /**
   * What `myWeekSummary` said the building is. NOT authoritative — see
   * {@link planLocationId}. Kept separate from the resolved answer so the two
   * can be compared rather than conflated.
   */
  private readonly serverLocationName = signal('');
  private readonly serverLocationId = signal('');
  protected readonly weekOffset = signal(0);
  protected readonly committing = signal(false);
  protected readonly commitProgress = signal<CommitProgress | null>(null);
  protected readonly result = signal<CommitResult | null>(null);
  private readonly commitStatus =
    viewChild.required<ElementRef<HTMLElement>>('commitStatus');
  /** Bookings created during THIS dialog session — drives resolve(true). */
  private readonly sessionBookedCount = signal(0);

  protected readonly maxWeekOffset = MAX_WEEK_OFFSET;

  /** The id the "no office day yet" reason is announced through. */
  protected readonly noOfficeReasonId = 'sb-fill-across-reason';

  /** Active language, so every derived label re-renders on a language switch. */
  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  /** British English dates: `en` means en-GB here ("Mon 21 Jul", not "Mon, Jul 21"). */
  private readonly dateLocale = computed(() =>
    this.lang() === 'en' ? 'en-GB' : this.lang(),
  );

  /** Localised, live-announced detail for the active commit stage. */
  protected readonly commitProgressLabel = computed(() => {
    void this.lang();
    const progress = this.commitProgress();
    if (!progress) return '';
    return this.transloco.translate(
      `shared.quick_book_dialog.progress_${progress.stage}`,
      {
        completed: progress.completed,
        total: progress.total,
      },
    );
  });

  /** Per-resource booking capability (customer feature flags). */
  protected readonly canDesk = this.featureFlags.isEnabled('desks.booking');
  protected readonly canParking = this.featureFlags.isEnabled('parking.booking');
  protected readonly canLocker = this.featureFlags.isEnabled('lockers.booking');

  // ── Which building are we planning? ────────────────────
  //
  // THE BUG THIS REPLACES. `myWeekSummary` resolves its building from
  // `user.roles[0].locationId` and reads that location back WITHOUT an
  // `enabled` filter (rdx-platform-api `BookingsService.getWeekSummary`), so a
  // viewer whose first role still points at a retired site gets that site —
  // which is how the header came to read "(Inactive) Warsaw". It is not a
  // cosmetic defect: the same `locationId` is what the planner hands to
  // `AutoBookService`, so every desk would have been auto-picked in the wrong
  // building.
  //
  // The app already knows which building the viewer is in. That answer wins;
  // the server's is the fallback. The real fix is a `locationId` argument on
  // `myWeekSummary` (reported as owed) so the DAYS come from the same building
  // as the header — today the days are the viewer's bookings wherever they are,
  // which is at least not contradicted by either answer.

  /** The building the planner plans against. Viewer's active site wins. */
  protected readonly planLocationId = computed(
    () => this.locationState.selectedLocationId() || this.serverLocationId(),
  );

  /**
   * The building's name, or '' when we cannot back one.
   *
   * When the viewer's active site is the one the server named, the server's
   * name is fine. When they differ, the server's name belongs to a DIFFERENT
   * building and must not be printed next to the active one — we print the
   * name from the locations list, or nothing at all.
   */
  protected readonly planLocationName = computed(() => {
    const planned = this.planLocationId();
    if (!planned) return '';
    const known = this.platformData.locationsList().find(l => l._id === planned);
    if (known?.name) return known.name;
    return planned === this.serverLocationId() ? this.serverLocationName() : '';
  });

  // ── Derived surface ────────────────────────────────────

  /**
   * The resources this building actually offers.
   *
   * Desks, parking and lockers each have a customer feature flag; the flag is
   * the NECESSARY condition and the building's own inventory is the
   * sufficient one. Until the inventory has loaded the flags alone decide,
   * which is why {@link ensureInventory} is now AWAITED before the plan is
   * seeded — the previous fire-and-forget call meant the office-day rows could
   * be rendered, and the Locker chip decided, against an empty inventory.
   *
   * `lockers.booking` is still FE-declared and resolves OFF until the backend
   * adds the catalogue key, so the inventory is what actually surfaces the
   * Locker chip today. That is the honest ladder: a building that stocks
   * bookable lockers offers them; one that does not, does not.
   */
  protected readonly offeredKinds = computed<ResourceKind[]>(() => {
    const locId = this.planLocationId();
    const spaces = locId ? this.platformData.spacesMapFor(locId) : null;
    const inventoryKnown = !!spaces && spaces.size > 0;
    const stocks = (kind: ResourceKind): boolean => {
      if (!spaces) return false;
      for (const space of spaces.values()) {
        if (space.bookable && matchesAutoBookKind(space.spaceType, kind)) return true;
      }
      return false;
    };

    const offered: ResourceKind[] = [];
    if (this.canDesk() && (!inventoryKnown || stocks('desk'))) offered.push('desk');
    if (this.canParking() && (!inventoryKnown || stocks('parking'))) offered.push('parking');
    if (inventoryKnown ? stocks('locker') : this.canLocker()) offered.push('locker');
    return offered;
  });

  /** "Mon 21 Jul – Fri 25 Jul" — the loaded week's working span. */
  protected readonly weekLabel = computed(() =>
    weekRangeLabel(this.days(), this.dateLocale()),
  );

  /** "Mon 21 Jul – Fri 25 Jul · Birmingham Rum Runner Works". The building is
   *  omitted when we cannot name one — never invented, never the wrong one. */
  protected readonly subtitle = computed(() =>
    [this.weekLabel(), this.planLocationName()].filter(Boolean).join(' · '),
  );

  /**
   * The day the planner tints — the design's "selected day" row. Derived, so
   * it cannot drift from the rows: the caller's preset day if there is one,
   * else today, else the first day still plannable.
   */
  protected readonly focusDate = computed(() =>
    focusedDate(this.days(), this.quickBookService.options().date),
  );

  /**
   * "the desk, parking and locker" — built from the resources this building
   * actually offers, so the intro never promises a locker to a tenant that
   * has none.
   */
  protected readonly offeredResourcePhrase = computed(() => {
    const nouns = this.offeredKinds().map(kind =>
      this.transloco.translate(`shared.quick_book_dialog.resource_noun.${kind}`),
    );
    if (nouns.length === 0) return '';
    return new Intl.ListFormat(this.dateLocale(), {
      style: 'long',
      type: 'conjunction',
    }).format(nouns);
  });

  /** THE office-day count. Every "applies to" and footer figure reads this one. */
  protected readonly officeDayCount = computed(() => officeDays(this.days()).length);

  /** THE list of resources the commit will book. The action's number is its length. */
  protected readonly planned = computed(() => plannedBookings(this.days()));

  protected readonly bookingCount = computed(() => this.planned().length);

  /** Days whose presence differs from the loaded state. */
  protected readonly presenceChangeCount = computed(
    () => presenceDeltas(this.days()).length,
  );

  /** Anything to commit at all — drives the primary action's enabled state. */
  protected readonly hasChanges = computed(
    () => this.bookingCount() + this.presenceChangeCount() > 0,
  );

  /**
   * The days the viewer's favourite people are mostly in, e.g. "Tue & Wed" —
   * built from `colleaguesInOffice`. Empty when no favourite has booked this
   * week, and then the intro says nothing about colleagues at all.
   */
  protected readonly smartHintDays = computed(() => topColleagueDayLabels(this.days()));

  /**
   * Presence segments per day, rebuilt when the plan or the language changes.
   *
   * PAST WEEKDAYS GET A ROW TOO. The design shows the whole working week, and
   * collapsing Monday to the word "Past" on a Thursday loses both the shape of
   * the week and the fact that Monday held a desk. So a past weekday renders
   * the same four-way control with every segment disabled and the reason
   * linked — appearance, accessible state and behaviour agreeing, which is the
   * same bar §11b sets for the locked case. Weekends stay inert: they were
   * never plannable, so there is no control to disable.
   */
  protected readonly segmentsByDate = computed(() => {
    void this.lang();
    const byDate = new Map<string, SbSegment[]>();
    for (const day of this.days()) {
      if (day.isWeekend) continue;
      byDate.set(
        day.date,
        PRESENCE_OPTIONS.map(option => ({
          value: option,
          label: this.transloco.translate(`home_preview.your_week.presence.${option}`),
          disabled: day.isPast || presenceIsLocked(day, option),
        })),
      );
    }
    return byDate;
  });

  constructor() {
    effect(() => {
      if (this.quickBookService.open()) {
        const opts = this.quickBookService.options();
        this.weekOffset.set(0);
        this.result.set(null);
        this.commitProgress.set(null);
        this.sessionBookedCount.set(0);
        this.analytics.capture(ANALYTICS_EVENTS.SMART_BOOK_OPENED, {
          source: opts.source ?? 'unknown',
          preset_date: opts.date,
          preset_kind: opts.kind,
          location_id: this.locationState.selectedLocationId() ?? undefined,
        });
        void this.openToFirstPlannableWeek();
      }
    });
  }

  /**
   * Initial load: start on the current week, but when every weekday is
   * already in the past (opened on a weekend / Friday evening) advance to
   * NEXT week — there's nothing left to plan behind us.
   *
   * ORDER MATTERS. The inventory is AWAITED before the plan is seeded, because
   * `offeredKinds()` is what decides whether an office day defaults to a desk
   * and whether the Locker chip exists at all. Seeding first would have
   * defaulted against an empty inventory. The caller's preset lands before the
   * seed so a preset day gets the same office default as every other office
   * day — one rule, no special case.
   */
  private async openToFirstPlannableWeek(): Promise<void> {
    await this.loadWeek(0);
    if (!this.loadError() && !this.days().some(isEditable)) {
      this.weekOffset.set(1);
      await this.loadWeek(1);
    }
    await this.ensureLocationNames();
    await this.ensureInventory();
    this.applyOpenPreset();
    this.seedDefaults();
  }

  // ── Week navigation ───────────────────────────────────

  protected prevWeek(): void {
    const offset = this.weekOffset() - 1;
    if (offset < 0) return;
    this.clearCommitFeedback();
    this.weekOffset.set(offset);
    void this.loadWeek(offset).then(() => this.seedDefaults());
  }

  protected nextWeek(): void {
    const offset = this.weekOffset() + 1;
    if (offset > MAX_WEEK_OFFSET) return;
    this.clearCommitFeedback();
    this.weekOffset.set(offset);
    void this.loadWeek(offset).then(() => this.seedDefaults());
  }

  protected retryLoad(): void {
    void this.loadWeek(this.weekOffset()).then(() => this.seedDefaults());
  }

  /**
   * Apply the §11b office-day default (a desk, unless the day already holds
   * one) to a freshly-loaded week.
   *
   * Deliberately NOT called from `loadWeek`: the reload that follows a partly
   * failed commit must not re-tick a request the server has just refused.
   */
  private seedDefaults(): void {
    const offered = this.offeredKinds();
    this.days.update(days => seedOfficeDefaults(days, offered));
  }

  // ── Plan editing ──────────────────────────────────────

  protected segmentsFor(day: PlanDay): SbSegment[] {
    return this.segmentsByDate().get(day.date) ?? [];
  }

  /**
   * Why this day's segments are locked. Two different locks, two different
   * reasons — a past day cannot be re-planned at all, a booked day cannot be
   * declared away from Office until the booking is released.
   *
   * The past reason reuses the existing `past` string rather than adding a
   * key: `public/i18n/*.json` is outside this lane, and a key added on one
   * side of the EN↔JA parity gate is a broken build for everyone else.
   */
  protected lockReason(day: PlanDay): string {
    return this.transloco.translate(
      day.isPast
        ? 'shared.quick_book_dialog.past'
        : 'shared.quick_book_dialog.presence_locked',
    );
  }

  /** The tinted row — the design's selected day. */
  protected isFocused(day: PlanDay): boolean {
    return day.date === this.focusDate();
  }

  /**
   * Which resource kinds this row renders. An office day renders every kind
   * the building offers (held or choosable); a PAST day renders only what it
   * actually held — those are facts about the week, and hiding them makes the
   * week look emptier than it was. Nothing is choosable on a past day.
   */
  protected resourceKindsFor(day: PlanDay): readonly ResourceKind[] {
    if (!day.isPast) return this.offeredKinds();
    return RESOURCE_KINDS.filter(kind => day.held[kind] !== null);
  }

  /** Does this row carry a resource line at all? */
  protected showsResources(day: PlanDay): boolean {
    if (day.isWeekend) return false;
    if (day.isPast) return holdsAnything(day);
    return isOfficeDay(day) && this.offeredKinds().length > 0;
  }

  /** Stroke-icon name for a resource kind. */
  protected resourceIcon(kind: ResourceKind): string {
    return kind === 'parking' ? 'parking' : kind === 'locker' ? 'locker' : 'desk';
  }

  protected heldName(day: PlanDay, kind: ResourceKind): string {
    return day.held[kind]?.spaceName ?? '';
  }

  protected isHeld(day: PlanDay, kind: ResourceKind): boolean {
    return day.held[kind] !== null;
  }

  protected wants(day: PlanDay, kind: ResourceKind): boolean {
    return day.want[kind];
  }

  protected setPresence(day: PlanDay, presence: string): void {
    this.clearCommitFeedback();
    const offered = this.offeredKinds();
    this.days.update(days =>
      withPresence(days, day.date, presence as Presence, offered),
    );
  }

  protected onToggleResource(day: PlanDay, kind: ResourceKind): void {
    this.clearCommitFeedback();
    this.days.update(days => toggleResource(days, day.date, kind));
  }

  protected acrossPressed(kind: ResourceKind): boolean {
    return fillAcrossPressed(this.days(), kind);
  }

  /** Fill across — one resource, every office day, in one press. */
  protected onFillAcross(kind: ResourceKind): void {
    if (this.officeDayCount() === 0) return;
    this.clearCommitFeedback();
    const on = !this.acrossPressed(kind);
    this.days.update(days => fillAcross(days, kind, on));
    this.analytics.capture(ANALYTICS_EVENTS.SMART_BOOK_FILL_ACROSS, {
      kind,
      on,
      office_days: this.officeDayCount(),
      location_id: this.planLocationId() || undefined,
    });
  }

  /**
   * Fill down is offered only from a day that can itself be planned. A past
   * day now renders a full row, and a control that renders but refuses the
   * click is the exact defect §11b calls out — so it does not render.
   */
  protected canFillDownFrom(day: PlanDay): boolean {
    return isEditable(day) && canFillDown(this.days(), day.date);
  }

  /** Fill down — copy this day's whole setup to the rest of the week. */
  protected onFillDown(day: PlanDay): void {
    const presence = day.presence;
    if (!presence) return;
    this.clearCommitFeedback();
    const before = this.days();
    const after = fillDown(before, day.date);
    this.days.set(after);
    this.analytics.capture(ANALYTICS_EVENTS.SMART_BOOK_FILL_DOWN, {
      from_date: day.date,
      days_filled: after.filter((d, i) => d !== before[i]).length,
      presence,
      location_id: this.planLocationId() || undefined,
    });
  }

  /**
   * Smart-fill — pre-select Office + Desk using the colleague signal: the
   * weekdays where the viewer's favourite people are in win; when no
   * favourite has booked, every remaining weekday is filled. Deliberate
   * Home / Out choices are always respected.
   */
  protected smartFill(): void {
    this.clearCommitFeedback();
    const outcome = applySmartFill(this.days(), this.canDesk());
    this.days.set(outcome.days);

    this.analytics.capture(ANALYTICS_EVENTS.SMART_BOOK_SMART_FILL, {
      days_toggled: outcome.toggled,
      used_colleague_signal: outcome.usedColleagueSignal,
      location_id: this.planLocationId() || undefined,
    });
  }

  // ── Commit (presence deltas + one auto-book batch) ─────

  protected async commit(): Promise<void> {
    if (this.committing() || !this.hasChanges()) return;
    this.committing.set(true);
    this.result.set(null);
    queueMicrotask(() => this.commitStatus().nativeElement.focus());

    try {
      const locId = this.planLocationId() || undefined;
      const days = this.days();
      const labelFor = (date?: string) =>
        days.find(d => d.date === date)?.dayLabel || date || '';

      // 1) Persist presence DELTAS only (unchanged days cost no round trip).
      const presenceChanges = presenceDeltas(days);
      let presencePersisted = 0;
      // Each failed day keeps the server's reason when it refused the day.
      const failedPresence: { date: string; reason: string | null }[] = [];
      if (presenceChanges.length > 0) {
        this.commitProgress.set({
          stage: 'presence',
          completed: 0,
          total: presenceChanges.length,
        });
      }
      for (const d of presenceChanges) {
        const result = await this.presenceWriter.persistResult(d.date, d.presence, locId);
        this.commitProgress.set({
          stage: 'presence',
          completed: presencePersisted + failedPresence.length + 1,
          total: presenceChanges.length,
        });
        if (!result.ok) {
          failedPresence.push({ date: d.date, reason: presenceRefusal(result) });
          continue;
        }
        presencePersisted++;
        this.analytics.capture(ANALYTICS_EVENTS.HOME_PREVIEW_PRESENCE_SET, {
          presence: d.presence,
          date: d.date,
          source: 'smart-book',
          location_id: locId,
        });
      }

      // 2) Auto-pick + book every requested resource in ONE batch round trip.
      //    `planned()` is the SAME list the footer counted.
      const plans: readonly AutoBookPlan[] = this.planned();
      const results = await this.autoBook.autoBookWeek(
        [...plans],
        locId,
        (progress) => this.commitProgress.set(progress),
      );

      // 3) Bucket the outcomes: created / already-booked (kept) / failed.
      let created = 0;
      const skippedDays: string[] = [];
      const failures: string[] = [];

      for (const res of results) {
        if (res.success) {
          created++;
          this.sessionBookedCount.update(n => n + 1);
          this.analytics.capture(ANALYTICS_EVENTS.SMART_BOOK_DAY_BOOKED, {
            kind: res.kind ?? 'desk',
            date: res.date ?? '',
            auto: true,
            location_id: locId,
          });
        } else if (res.skipped) {
          skippedDays.push(labelFor(res.date));
        } else {
          failures.push(
            this.transloco.translate('shared.quick_book_dialog.result_failure', {
              reason: res.error ?? this.transloco.translate('shared.smart_book.auto_book_error.failed'),
              day: labelFor(res.date),
            }),
          );
        }
      }
      for (const { date, reason } of failedPresence) {
        failures.push(
          this.transloco.translate('shared.quick_book_dialog.result_failure', {
            reason:
              reason ??
              this.transloco.translate('shared.quick_book_dialog.presence_failure_reason'),
            day: labelFor(date),
          }),
        );
      }

      this.analytics.capture(ANALYTICS_EVENTS.SMART_BOOK_WEEK_CONFIRMED, {
        desks_requested: plans.filter(p => p.kind === 'desk').length,
        parking_requested: plans.filter(p => p.kind === 'parking').length,
        lockers_requested: plans.filter(p => p.kind === 'locker').length,
        presence_changes: presenceChanges.length,
        created,
        skipped: skippedDays.length,
        failed: failures.length,
        week_offset: this.weekOffset(),
        location_id: locId,
      });

      if (created > 0) {
        this.orchestrator.refreshAvailabilityAndColors().catch(() => {});
      }

      // 4) Clean run → close (callers refresh + toast); failures → stay open
      //    with the banner over fresh data so the user can read what missed.
      if (failures.length === 0) {
        void this.liveAnnouncer.announce(
          this.transloco.translate(
            'shared.quick_book_dialog.completion_announcement',
            {
              booked: created,
              days: presencePersisted,
            },
          ),
          'polite',
        );
        this.quickBookService.resolve(this.sessionBookedCount() > 0);
        return;
      }

      const uniqueSkipped = [...new Set(skippedDays.filter(Boolean))];
      this.result.set({
        summary: this.transloco.translate('shared.quick_book_dialog.result_summary', {
          booked: created,
          days: presencePersisted,
        }),
        skipped: uniqueSkipped.length > 0
          ? this.transloco.translate('shared.quick_book_dialog.result_skipped', {
              days: uniqueSkipped.join(', '),
            })
          : '',
        failures,
      });
      await this.loadWeek(this.weekOffset());
    } finally {
      this.commitProgress.set(null);
      this.committing.set(false);
    }
  }

  private clearCommitFeedback(): void {
    this.result.set(null);
  }

  // ── Close ─────────────────────────────────────────────

  protected close(): void {
    if (this.committing()) return;
    this.quickBookService.resolve(this.sessionBookedCount() > 0);
  }

  // ── Data loading ──────────────────────────────────────

  private async loadWeek(offset: number): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const monday = this.getMonday(offset);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);

      const result = await queryWeekSummary<WeekSummaryDto>(this.graphql, WEEK_SUMMARY_QUERY_IN_OFFICE, WEEK_SUMMARY_QUERY, {
        startDate: localDateStr(monday),
        endDate: localDateStr(sunday),
        timezoneId: tz,
      }, 'network-only');

      if (isErr(result)) {
        this.days.set([]);
        this.loadError.set(result.error);
        return;
      }
      if (!result.data) {
        this.days.set([]);
        this.loadError.set(
          makeError('SERVER', this.transloco.translate('shared.quick_book_dialog.load_failed')),
        );
        return;
      }

      const today = localDateStr(new Date());
      this.days.set(
        (result.data.days ?? []).map((d) =>
          withConfirmedPresence(
            toPlanDay(d, today),
            this.presenceWriter.confirmedPresenceFor(d.date),
          ),
        ),
      );
      this.serverLocationName.set(result.data.locationName || '');
      this.serverLocationId.set(result.data.locationId || '');
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Make sure we can NAME the building we are planning against. Without the
   * locations list the header can only echo whatever `myWeekSummary` said —
   * which is exactly the answer we stopped trusting.
   */
  private async ensureLocationNames(): Promise<void> {
    if (this.platformData.locationsList().length > 0) return;
    await this.platformData.loadLocations();
  }

  /**
   * Hydrate the building's space inventory (never repointing the app's active
   * location). Two things need it: the resource chips only render for kinds
   * the building genuinely stocks, and the commit's auto-picker needs the same
   * inventory anyway — loading it here just moves the cost off the critical
   * path.
   */
  private async ensureInventory(): Promise<void> {
    const locId = this.planLocationId();
    if (!locId || this.platformData.spacesMapFor(locId).size > 0) return;
    await this.platformData.loadLocation(locId, { setActive: false });
  }

  /**
   * Pre-select the caller's requested (date, kind) — e.g. the day popover's
   * "Book parking" lands with that day already Office + Parking, one press
   * from committing. A kind with no date (widget "Book parking") targets the
   * first plannable day of the week (today onwards).
   */
  private applyOpenPreset(): void {
    const opts = this.quickBookService.options();
    if (!opts.date && !opts.kind) return;
    const kind: ResourceKind = opts.kind ?? 'desk';
    if (kind !== 'locker') {
      const flagKey = kind === 'desk' ? 'desks.booking' : 'parking.booking';
      if (!this.featureFlags.is(flagKey)) return;
    }

    this.days.update(days => applyPreset(days, opts.date, kind));
  }

  /**
   * Monday of the requested week, anchored at local noon so a timezone behind
   * UTC cannot roll the date back a day (CLAUDE.md § Timezone).
   */
  private getMonday(weekOffset: number): Date {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now);
    monday.setDate(diff + weekOffset * 7);
    monday.setHours(12, 0, 0, 0);
    return monday;
  }
}
